import { Box, CircularProgress, TextField } from '@mui/material';
import React from 'react';
import { StyledButton } from '../styles/StyledButtons';
import {
  AdvisingCallout,
  EOIConfirmCheckbox,
  EOIInvestmentInput,
  hasAdvisingFor,
  ReferrerLegalCheckbox,
} from './EOIFormControls';
import type { EOISectionProps } from './eoiTypes';
import { EOIIntroText, EOITitle, EOITypography } from './EOISettingsContext';
import type { EOISectionState } from './useEOISectionState';

type ReferrerPanelProps = Pick<
  EOISectionProps,
  'advisingFor' | 'isCustodian' | 'acceptingInvitation'
> &
  Pick<
    EOISectionState,
    | 'investmentInput'
    | 'validationError'
    | 'confirmed'
    | 'referrerFirstName'
    | 'referrerLastName'
    | 'referrerMobile'
    | 'referrerEoiConfirmed'
    | 'canAcceptInvitation'
    | 'investmentMessage'
    | 'submitting'
    | 'rejecting'
    | 'isObserver'
    | 'setInvestmentInput'
    | 'setConfirmed'
    | 'setReferrerFirstName'
    | 'setReferrerLastName'
    | 'setReferrerMobile'
    | 'setReferrerEoiConfirmed'
    | 'handleAcceptInvitation'
    | 'handleRejectInvitation'
  >;

type InitialPanelProps = Pick<EOISectionProps, 'advisingFor' | 'isCustodian'> &
  Pick<
    EOISectionState,
    | 'investmentInput'
    | 'validationError'
    | 'confirmed'
    | 'validInvestment'
    | 'investmentMessage'
    | 'submitting'
    | 'suggesting'
    | 'isObserver'
    | 'hasSubmitted'
    | 'setInvestmentInput'
    | 'setConfirmed'
    | 'handleSubmit'
  >;

export const EOISectionReferrerPanel: React.FC<ReferrerPanelProps> = ({
  advisingFor,
  isCustodian = false,
  acceptingInvitation = false,
  investmentInput,
  validationError,
  confirmed,
  referrerFirstName,
  referrerLastName,
  referrerMobile,
  referrerEoiConfirmed,
  canAcceptInvitation,
  investmentMessage,
  submitting,
  rejecting,
  isObserver,
  setInvestmentInput,
  setConfirmed,
  setReferrerFirstName,
  setReferrerLastName,
  setReferrerMobile,
  setReferrerEoiConfirmed,
  handleAcceptInvitation,
  handleRejectInvitation,
}) => (
  <>
    <EOITitle />
    <EOIIntroText />
    <EOITypography>
      Complete your details below to accept this invitation and create your account.
    </EOITypography>
    <EOITypography>
      If you would like to sign up without submitting an expression of interest, please leave the
      Indicative Investment field blank.
    </EOITypography>
    <Box>
      <TextField
        fullWidth
        label="First Name"
        value={referrerFirstName}
        onChange={(e) => setReferrerFirstName(e.target.value)}
        required
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Last Name"
        value={referrerLastName}
        onChange={(e) => setReferrerLastName(e.target.value)}
        required
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Mobile"
        value={referrerMobile}
        onChange={(e) => setReferrerMobile(e.target.value)}
        required
        placeholder="+61400000000"
        sx={{ mb: 0, pb: '1.25rem' }}
      />
    </Box>
    {hasAdvisingFor(advisingFor) && isObserver ? (
      <AdvisingCallout advisingFor={advisingFor} variant="suggesting" />
    ) : hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="submitting" />
    ) : null}
    <ReferrerLegalCheckbox
      confirmed={confirmed}
      onChange={setConfirmed}
      isCustodian={isCustodian}
      sx={hasAdvisingFor(advisingFor) ? { mt: 2 } : undefined}
    />
    <EOIConfirmCheckbox
      confirmed={referrerEoiConfirmed}
      onChange={setReferrerEoiConfirmed}
      isCustodian={isCustodian}
      sx={{ mt: 2 }}
    />
    <EOITypography>{investmentMessage}</EOITypography>
    <EOIInvestmentInput
      investmentInput={investmentInput}
      validationError={validationError}
      editable={referrerEoiConfirmed}
      isCustodian={isCustodian}
      onChange={setInvestmentInput}
    />
    <Box className="eoiButtons hide-print" mt={2} display="flex" gap={2}>
      <StyledButton
        disabled={!canAcceptInvitation || submitting || rejecting || acceptingInvitation}
        onClick={handleAcceptInvitation}
      >
        {submitting || acceptingInvitation ? (
          <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
        ) : (
          'Accept Invitation'
        )}
      </StyledButton>
      <StyledButton
        buttonVariant="inactiveOrCancel"
        disabled={submitting || rejecting}
        onClick={handleRejectInvitation}
      >
        {rejecting ? (
          <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
        ) : (
          'Decline Invitation'
        )}
      </StyledButton>
    </Box>
  </>
);

export const EOISectionInitialPanel: React.FC<InitialPanelProps> = ({
  advisingFor,
  isCustodian = false,
  investmentInput,
  validationError,
  confirmed,
  validInvestment,
  investmentMessage,
  submitting,
  suggesting,
  isObserver,
  hasSubmitted,
  setInvestmentInput,
  setConfirmed,
  handleSubmit,
}) => (
  <>
    <EOITitle />
    <EOIIntroText />
    {hasAdvisingFor(advisingFor) && isObserver ? (
      <AdvisingCallout advisingFor={advisingFor} variant="suggesting" />
    ) : hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="submitting" />
    ) : null}
    {!isObserver && (
      <EOIConfirmCheckbox
        confirmed={confirmed}
        onChange={setConfirmed}
        isCustodian={isCustodian}
        sx={hasAdvisingFor(advisingFor) ? { mt: 2 } : undefined}
      />
    )}
    <EOITypography>{investmentMessage}</EOITypography>
    <EOIInvestmentInput
      investmentInput={investmentInput}
      validationError={validationError}
      editable
      isCustodian={isCustodian}
      onChange={setInvestmentInput}
    />
    <Box className="eoiButtons hide-print" mt={2} display="flex" gap={2}>
      <StyledButton
        disabled={
          isCustodian || !validInvestment || (!isObserver && !confirmed) || submitting || suggesting
        }
        onClick={handleSubmit}
      >
        {(submitting || suggesting) && (
          <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
        )}
        {isObserver ? (hasSubmitted ? 'Suggest an Update' : 'Suggest EOI') : 'Submit EOI'}
      </StyledButton>
    </Box>
  </>
);

export const EOISectionWithdrawPanel: React.FC<{
  advisingFor?: string | null;
  isCustodian?: boolean;
  withdrawing: boolean;
  setWithdrawMode: (value: boolean) => void;
  handleWithdraw: () => void;
}> = ({ advisingFor, isCustodian = false, withdrawing, setWithdrawMode, handleWithdraw }) => (
  <>
    <EOITitle>Withdraw Expression of Interest</EOITitle>
    <EOITypography>
      Please confirm that you wish to withdraw your Expression of Interest.
    </EOITypography>
    {hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="withdrawing" />
    ) : null}
    <Box className="eoiButtons two-buttons" display="flex" gap={2}>
      <StyledButton onClick={() => setWithdrawMode(false)}>Keep EOI</StyledButton>
      <StyledButton
        buttonVariant="inactiveOrCancel"
        disabled={isCustodian}
        onClick={handleWithdraw}
      >
        {withdrawing && <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />}
        Withdraw EOI
      </StyledButton>
    </Box>
  </>
);

export const EOISectionUpdatePanel: React.FC<{
  advisingFor?: string | null;
  isCustodian?: boolean;
  investmentInput: string | undefined;
  validationError: string | undefined;
  confirmed: boolean;
  validInvestment: number | undefined;
  investmentMessage: string;
  submitting: boolean;
  setInvestmentInput: (value: string | undefined) => void;
  setUpdateMode: (value: boolean) => void;
  handleSubmit: () => void;
}> = ({
  advisingFor,
  isCustodian = false,
  investmentInput,
  validationError,
  confirmed,
  validInvestment,
  investmentMessage,
  submitting,
  setInvestmentInput,
  setUpdateMode,
  handleSubmit,
}) => (
  <>
    <EOITitle>Update Expression of Interest</EOITitle>
    <EOIIntroText />
    {hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="updating" />
    ) : null}
    <EOIConfirmCheckbox
      confirmed={confirmed}
      onChange={() => {}}
      disabled
      isCustodian={isCustodian}
    />
    <EOITypography>{investmentMessage}</EOITypography>
    <EOIInvestmentInput
      investmentInput={investmentInput}
      validationError={validationError}
      editable
      isCustodian={isCustodian}
      onChange={setInvestmentInput}
    />
    <Box className="eoiButtons two-buttons" mt={2} display="flex" gap={2}>
      <StyledButton
        disabled={isCustodian || !validInvestment || !confirmed || submitting}
        onClick={handleSubmit}
      >
        {submitting && <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />}
        Confirm EOI
      </StyledButton>
      <StyledButton buttonVariant="inactiveOrCancel" onClick={() => setUpdateMode(false)}>
        Cancel Update
      </StyledButton>
    </Box>
  </>
);

export const EOISectionSubmittedPanel: React.FC<{
  advisingFor?: string | null;
  isCustodian?: boolean;
  isObserver: boolean;
  investmentInput: string | undefined;
  validationError: string | undefined;
  confirmed: boolean;
  investmentMessage: string;
  suggesting: boolean;
  setConfirmed: (value: boolean) => void;
  setUpdateMode: (value: boolean) => void;
  setWithdrawMode: (value: boolean) => void;
}> = ({
  advisingFor,
  isCustodian = false,
  isObserver,
  investmentInput,
  validationError,
  confirmed,
  investmentMessage,
  suggesting,
  setConfirmed,
  setUpdateMode,
  setWithdrawMode,
}) => (
  <>
    <EOITitle />
    <EOIIntroText />
    {hasAdvisingFor(advisingFor) ? (
      <AdvisingCallout advisingFor={advisingFor} variant="viewing" />
    ) : null}
    <EOIConfirmCheckbox
      confirmed={confirmed}
      onChange={setConfirmed}
      disabled
      isCustodian={isCustodian}
    />
    <EOITypography>{investmentMessage}</EOITypography>
    <EOIInvestmentInput
      investmentInput={investmentInput}
      validationError={validationError}
      editable={false}
      isCustodian={isCustodian}
      onChange={() => {}}
    />
    {isObserver ? (
      <Box sx={{ mt: 2 }}>
        <StyledButton onClick={() => setUpdateMode(true)} disabled={suggesting}>
          {suggesting && <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />}
          Suggest an Update
        </StyledButton>
      </Box>
    ) : (
      <Box className="eoiButtons two-buttons" mt={2} display="flex" gap={2}>
        <StyledButton
          disabled={isCustodian}
          onClick={() => {
            if (!isObserver && !isCustodian) setUpdateMode(true);
          }}
        >
          Update EOI
        </StyledButton>
        <StyledButton
          buttonVariant="inactiveOrCancel"
          className="destructive"
          disabled={isCustodian}
          onClick={() => {
            if (!isObserver && !isCustodian) setWithdrawMode(true);
          }}
        >
          Withdraw EOI
        </StyledButton>
      </Box>
    )}
  </>
);
